import { DISMISS, useNotification } from "collections";
import {
  DefaultModalForm,
  DeleteModal,
  useModalDispatch
} from "collections/Modal";
import {
  getSelectedWithoutHeader,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import {
  CancelButton,
  DropdownButton,
  DropdownList,
  LoadingIndicator,
  OptionType,
  PolyIcon,
  SubmitButton
} from "elements";
import {
  DeviceIn,
  PolicyDeploymentScope,
  useGroupsForDeviceQuery,
  usePoliciesQuery,
  useUpdateDeviceGroupPriorityMutation
} from "generated/graphql";
import { generatePath, infiniteNestLink, Link, useHistory } from "polyrouter";
import { needs, useGuard } from "rbac";
import React, { useContext, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import CreateDeviceGroupModal from "routes/Manage/DeviceGroups/Create/CreateDeviceGroupModal";
import {
  Group,
  useAddDevicesToGroup,
  useDeviceGroups,
  useRemoveDevicesFromGroup
} from "routes/Manage/DeviceGroups/DeviceGroupsUtils";
import { SortState } from "store/filters";
import { Guard, ProtectedRoute } from "TsRoleGuards";
import { OperationContext } from "urql";
import { move } from "utilities/arrayUtils";
import { useTranslation } from "utilities/i18n";
import { DetailProps } from "../DeviceDetails";
import "./GroupMembership.scss";
import { uniqBy } from "lodash";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

type GroupRowData = Group & {
  precedence: number;
  modelCount: number;
  deviceCount: number;
};

export const GroupMembership: React.FC<DetailProps & {
  showChecks?: boolean;
}> = ({ serialNumber, hardwareModel, showChecks = true }) => {
  const { t } = useTranslation();
  const { location } = useHistory();
  const dismissModal = useModalDispatch();
  const [addNotification] = useNotification();
  const removeDevicesFromGroup = useRemoveDevicesFromGroup();
  const [, updateGroupPrecedence] = useUpdateDeviceGroupPriorityMutation();
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [sort, setSort] = useState({
    field: "precedence",
    direction: "ASC"
  } as SortState);
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const [isRemoving, setRemoving] = useState(false);

  const serial = serialNumber?.toLowerCase() ?? "";
  const {
    data: groupsForDeviceData,
    fetching: groupsForDeviceFetching,
    stale: groupsForDeviceStale,
    executor: refreshGroupsForDevice
  } = useGroupsForDeviceQuery({
    variables: {
      serial,
      tenantId: tenantId
    }
  });
  const {
    data: groupsData,
    fetching: groupsFetching,
    stale: groupsStale,
    executor: refreshAllGroups
  } = useDeviceGroups();
  let groupIds =
    groupsForDeviceData?.getDevice?.groups?.filter(
      (groupId): groupId is string => groupId != null
    ) ?? [];
  const groupsMap: Record<string, Group> =
    groupsData?.reduce((groupsMap, group) => {
      groupsMap[group.id] = group;
      return groupsMap;
    }, {} as Record<string, Group>) ?? {};

  const groups: GroupRowData[] = groupIds
    .map((groupId, index) => {
      const group = groupsMap[groupId];
      if (!group) {
        return null;
      }
      const modelCount =
        group.devices.reduce(
          (models, { hardwareModel }) => models.add(hardwareModel ?? ""),
          new Set<string>()
        ).size ?? 0;

      return {
        ...group,
        precedence: index + 1,
        deviceCount: group.devices.length,
        modelCount
      };
    })
    .filter((group): group is GroupRowData => group !== null);

  sortTableData(groups, sort);

  const editRequirement = "UPDATE_SITES";
  const userCanEdit = useGuard(needs(editRequirement));

  const itemChecked = itemCheckedWithSet(selectedItems);
  const toggleItem = toggleItemWithSet(
    groups.length,
    selectedItems,
    setSelectedItems
  );
  const selectedGroups = getSelectedWithoutHeader(selectedItems);
  const anySelected = selectedGroups.length > 0;
  const selectedCount = selectedGroups.length;

  const headers = [
    {
      name: "precedence",
      title: t("PRECEDENCE")
    },
    {
      name: "name",
      title: t("GROUP_NAME")
    },
    {
      name: "modelCount",
      title: t("MODEL_COUNT")
    },
    {
      name: "policyCount",
      title: t("POLICY_COUNT")
    },
    {
      name: "deviceCount",
      title: t("DEVICE_COUNT")
    }
  ];

  const refresh = () => {
    const networkOnly: Partial<OperationContext> = {
      requestPolicy: "network-only"
    };
    refreshGroupsForDevice(networkOnly);
    refreshAllGroups(networkOnly);
  };

  const removeFromGroups = async () => {
    setRemoving(true);
    if (serial) {
      let errors = [];
      for (const groupId of selectedGroups) {
        const group = groups.filter(group => group.id === groupId)[0];
        const { data, error } = await removeDevicesFromGroup(group, [serial]);
        const response = data?.updateGroup;
        if (response?.error || error) {
          addNotification({
            type: "error",
            dismiss: DISMISS.DEFAULT,
            message: (response?.error || error?.message) ?? "err",
            persistThroughNav: true
          });
          errors.push(error);
        }
      }

      if (errors.length === 0) {
        addNotification({
          type: "success",
          dismiss: DISMISS.DEFAULT,
          message: t("SUCCESSFULLY_DELETED_DEVICE_FROM_GROUP", {
            count: selectedGroups.length
          }),
          persistThroughNav: true
        });
      }
    }

    dismissModal();
    setSelectedItems([]);
    refresh();
    setRemoving(false);
  };

  const changePrecedence = async (groupId: string, precedence: number) => {
    if (precedence <= 0 || precedence > groupIds.length) {
      return;
    }
    const currentIndex = groupIds.indexOf(groupId);
    if (currentIndex < 0 || currentIndex === precedence - 1) {
      return;
    }

    move<string>(groupIds, currentIndex, precedence - 1);

    const { data, error } = await updateGroupPrecedence({
      serial,
      groups: groupIds,
      tenantId: tenantId
    });

    if (error || (data != null && !data.updateDeviceGroupPriority)) {
      addNotification({
        type: "error",
        message: t("ERROR_UPDATING_DEVICE_GROUPS_PRECEDENCE"),
        dismiss: DISMISS.FAST
      });
    } else {
      refresh();
    }
  };

  const loading =
    isRemoving ||
    groupsForDeviceFetching ||
    groupsFetching ||
    groupsForDeviceStale ||
    groupsStale;
  const isMySettingsView = location.pathname.includes("/settings/my-settings");
  return (
    <>
      <p className="text-md">{t("GROUP_MEMBERSHIP_INFO")}</p>
      {!isMySettingsView && (
        <div className="grouping table-actions mb-1">
          <Guard requirement={editRequirement}>
            <DropdownButton
              className="btn-primary"
              title={t("ADD_DEVICE_TO_GROUP")}
            >
              <Link to={infiniteNestLink("/create-group")}>
                {t("CREATE_NEW_GROUP")}
              </Link>
              <Link to={infiniteNestLink("/add-to-group")}>
                {t("ADD_TO_EXISTING_GROUP")}
              </Link>
            </DropdownButton>
            <Link
              to={anySelected ? infiniteNestLink("/remove-from-group") : ""}
              className={`btn ml-5 ${!anySelected ? "disabled" : ""}`}
            >
              {t("REMOVE_FROM_GROUP")}
            </Link>
          </Guard>
        </div>
      )}
      <table
        className="table group-membership-table"
        data-testid="group-membership-table"
      >
        <TableBulkItemHeader
          showChecks={userCanEdit && showChecks}
          checked={itemChecked(HEADER_ID)}
          onCheck={() =>
            toggleAll(
              groups.map(group => group.id),
              selectedItems,
              setSelectedItems
            )
          }
        >
          {headers.map(({ name, title }) => (
            <TableHeaderItem
              fieldName={name}
              title={title}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
              key={name}
            />
          ))}
          {/* Placeholder column for group precedence reorder icons */}
          <th className="move-tools p-5 last-cell"></th>
        </TableBulkItemHeader>
        <DragDropContext
          onDragEnd={({ draggableId, destination }) => {
            if (destination) {
              changePrecedence(draggableId, destination.index + 1);
            }
          }}
        >
          <Droppable droppableId="groups">
            {(provided, snapshot) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
                loading={loading}
                cols={headers.length + 1}
                className={`${snapshot.isDraggingOver ? "is-dragging" : ""}`}
              >
                {groups.map((group, index) => (
                  <GroupRow
                    showChecks={userCanEdit && showChecks}
                    group={group}
                    checked={itemChecked(group.id)}
                    onCheck={() => toggleItem(group.id)}
                    changePrecedence={changePrecedence}
                    groupCount={groups.length}
                    dragInProgress={snapshot.isDraggingOver}
                    index={index}
                    key={group.id + index}
                  />
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </table>
      {!loading && !groups.length ? (
        <div className="mt-1 text-lg text-center">
          {t("NO_GROUPS_FOR_DEVICE")}
        </div>
      ) : null}
      <ProtectedRoute nest path="/create-group" requirement={editRequirement}>
        <CreateDeviceGroupModal
          devices={[{ serial, deviceModel: hardwareModel ?? "" }]}
          onSuccess={refresh}
        />
      </ProtectedRoute>
      <ProtectedRoute nest path="/add-to-group" requirement={editRequirement}>
        <AddToExistingGroupModal
          devices={[{ serial, deviceModel: hardwareModel ?? "" }]}
          onSuccess={refresh}
        />
      </ProtectedRoute>
      <ProtectedRoute
        nest
        path="/remove-from-group"
        requirement={editRequirement}
      >
        <DeleteModal
          action="REMOVE"
          name="RemoveFromGroupsModal"
          title={t("REMOVE_DEVICE_FROM_GROUP", { count: selectedCount })}
          onDelete={removeFromGroups}
          disabled={isRemoving}
          confirmationString={t("CONFIRM_REMOVE_DEVICE_FROM_GROUP", {
            count: selectedCount
          })}
        />
      </ProtectedRoute>
    </>
  );
};

const GroupRow: React.FC<{
  checked: boolean;
  onCheck: () => void;
  showChecks: boolean;
  changePrecedence: (groupId: string, precedence: number) => void;
  groupCount: number;
  group: GroupRowData;
  dragInProgress: boolean;
  index: number;
}> = ({
  group,
  checked,
  showChecks,
  onCheck,
  changePrecedence,
  groupCount,
  dragInProgress,
  index
}) => {
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const [showMoveTools, setShowMoveTools] = useState(false);

  // fetch policies for group and calculate policyCount
  // count Settings and SW Update policies for the same model as one
  const { data, fetching } = usePoliciesQuery({
    variables: {
      groupId: group.id,
      scope: PolicyDeploymentScope.Group,
      tenantId: tenantId
    }
  });
  const policyCount = uniqBy(
    data?.getPolicies?.filter(policy => !!policy && !!policy.deviceModel),
    "deviceModel"
  ).length;

  return (
    <Draggable key={group.id} draggableId={group.id} index={index}>
      {(provided, snapshot) => (
        <TableBulkItemRow
          {...provided.draggableProps}
          ref={provided.innerRef}
          showChecks={showChecks}
          checked={checked}
          onCheck={() => onCheck()}
          className={`group-membership-row ${
            snapshot.isDragging ? "dragging" : ""
          }`}
          style={{ ...provided.draggableProps.style, position: "static" }}
          onMouseEnter={() => {
            setShowMoveTools(!dragInProgress);
          }}
          onMouseLeave={() => setShowMoveTools(false)}
        >
          <td data-testid="group-precedence">{group.precedence}</td>
          <td>
            <Link to={`/manage/device-groups/details/${group.id}`}>
              {group.name}
            </Link>
          </td>
          <td>{group.modelCount}</td>
          <td>
            {fetching ? (
              <LoadingIndicator className="inline text-left" title=" " />
            ) : (
              <span data-testid="group-policy-count">{policyCount}</span>
            )}
          </td>
          <td>{group.deviceCount}</td>
          <td className="move-tools p-5">
            <span className={showMoveTools ? "" : "hidden"}>
              <span
                data-testid="move-top"
                onClick={() => changePrecedence(group.id, 1)}
              >
                <PolyIcon icon="move_top" />
              </span>
              <span
                data-testid="move-bottom"
                onClick={() => changePrecedence(group.id, groupCount)}
              >
                <PolyIcon icon="move_bottom" />
              </span>
              <span
                data-testid="move-up"
                onClick={() => changePrecedence(group.id, group.precedence - 1)}
              >
                <PolyIcon icon="move_up" />
              </span>
              <span
                data-testid="move-down"
                onClick={() => changePrecedence(group.id, group.precedence + 1)}
              >
                <PolyIcon icon="move_down" />
              </span>
              <span {...provided.dragHandleProps} data-testid="drag">
                <PolyIcon icon="move" />
              </span>
            </span>
          </td>
        </TableBulkItemRow>
      )}
    </Draggable>
  );
};

export const AddToExistingGroupModal: React.FC<{
  devices: DeviceIn[];
  onSuccess?: () => void;
}> = ({ devices, onSuccess }) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const dismissModal = useModalDispatch();
  const addDevicesToGroup = useAddDevicesToGroup();
  const [group, setGroup] = useState<Group | null>(null);

  const { data } = useDeviceGroups();
  const groups = data ?? [];

  // for each device find out which groups are available to be added to
  const availableGroups = new Set<string>();
  devices.forEach(device => {
    groups
      .filter(
        group =>
          !group.devices.some(
            d => d.serialNumber?.toLowerCase() === device.serial.toLowerCase()
          )
      )
      .forEach(group => {
        availableGroups.add(group.id);
      });
  });

  const groupOptions: OptionType[] = groups.map(group => ({
    text: group.name,
    value: group.id,
    disabled: !availableGroups.has(group.id)
  }));

  const onSubmit = async () => {
    if (!group) {
      return;
    }
    const {
      result: { data, error },
      added
    } = await addDevicesToGroup(group, devices);
    const response = data?.updateGroup;
    if (response?.success) {
      const alreadyInGroup = devices.length - added;
      const successMessage =
        alreadyInGroup > 0
          ? t("ADD_TO_EXISTING_GROUP_SUCCESS_SOME_ALREADY_IN", {
              added,
              alreadyInGroup
            })
          : t("ADD_TO_EXISTING_GROUP_SUCCESS", { count: added });

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: (
          <span>
            {successMessage}
            &nbsp;
            <Link
              className="font-bold"
              to={generatePath("/manage/device-groups/details/:id", {
                id: group.id
              })}
            >
              {t("VIEW_GROUP")}
            </Link>
          </span>
        ),
        persistThroughNav: true
      });

      onSuccess && onSuccess();
      dismissModal();
    } else if (response?.error || error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: (response?.error || error?.message) ?? "err"
      });
    }
  };

  return (
    <DefaultModalForm
      title={t("ADD_TO_EXISTING_GROUP")}
      onSubmit={onSubmit}
      name={"AddToExistingGroupModal"}
      className="add-to-group-modal"
    >
      {({ isSubmitting }) => (
        <>
          <p className="small-text mt-15 mb-15">
            {t("ADD_TO_EXISTING_GROUP_NOTE")}
          </p>
          <div className="groups-dropdown half-width">
            <PolyIcon icon="search" />
            <DropdownList
              name="group"
              value={group?.id}
              onChange={groupId =>
                setGroup(groups.filter(group => group.id === groupId)[0])
              }
              options={groupOptions}
              placeholder={t("GROUP_NAME")}
              search
              inline
            />
          </div>
          <div className="btn-container">
            <CancelButton onClick={dismissModal} />
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!group}
              testId="add"
              labelKey="ADD"
              submittingLabelKey="SAVING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};
